import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Star, Quote, BadgeCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { API, CLINIC } from "@/lib/api";
import { ReviewDialog } from "./ReviewDialog";

const COLORS = ["bg-[#ebfbfa]", "bg-[#fef7e5]", "bg-[#f3f0ff]", "bg-[#fff1f1]", "bg-[#e0f2fe]"];

const Card = ({ r, i }) => (
  <div data-testid="review-card" className={`flex w-[85%] shrink-0 snap-start flex-col rounded-[1.75rem] p-7 sm:w-[380px] ${COLORS[i % COLORS.length]}`}>
    <Quote className="h-8 w-8 text-teal-600/40" />
    <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-700">"{r.text}"</p>
    <div className="mt-5 flex items-center justify-between">
      <div>
        <p className="font-display font-semibold text-slate-800">{r.name}</p>
        <p className="flex items-center gap-1 text-xs font-semibold text-slate-500">
          <BadgeCheck className="h-3.5 w-3.5 text-teal-600" />{r.source === "google" ? "Google review" : "Verified parent"}
        </p>
      </div>
      <div className="flex">{[...Array(r.rating)].map((_, k) => <Star key={k} className="h-4 w-4 fill-[#f5b82e] text-[#f5b82e]" />)}</div>
    </div>
  </div>
);

export const Reviews = () => {
  const [items, setItems] = useState([]);
  const track = useRef(null);
  useEffect(() => { axios.get(`${API}/reviews`).then((r) => setItems(r.data.items)).catch(() => {}); }, []);
  const slide = (dir) => track.current?.scrollBy({ left: dir * (track.current.clientWidth * 0.8), behavior: "smooth" });

  return (
    <section id="reviews" className="overflow-hidden py-24" data-testid="reviews-section">
      <div className="mx-auto flex max-w-7xl flex-col justify-between gap-6 px-6 md:flex-row md:items-end lg:px-8">
        <div>
          <span className="text-xs font-bold uppercase tracking-widest text-teal-600">Parents love us</span>
          <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
            {CLINIC.rating}<Star className="mx-2 inline h-8 w-8 fill-[#f5b82e] text-[#f5b82e]" />from {CLINIC.reviewCount} Google reviews
          </h2>
        </div>
        <div className="flex flex-wrap gap-3">
          <ReviewDialog />
          <a href={CLINIC.maps} target="_blank" rel="noreferrer" data-testid="google-reviews-link"
            className="btn-pop rounded-full border-2 border-slate-800 px-6 py-3 text-sm font-bold text-slate-800 hover:bg-slate-50">See on Google</a>
        </div>
      </div>
      <div className="mx-auto mt-10 max-w-7xl px-6 lg:px-8">
        <div className="mb-4 flex justify-end gap-2">
          <button onClick={() => slide(-1)} aria-label="Previous reviews" data-testid="reviews-prev-button"
            className="btn-pop flex h-11 w-11 items-center justify-center rounded-full border-2 border-slate-800 bg-white text-slate-800"><ChevronLeft className="h-5 w-5" /></button>
          <button onClick={() => slide(1)} aria-label="Next reviews" data-testid="reviews-next-button"
            className="btn-pop flex h-11 w-11 items-center justify-center rounded-full border-2 border-slate-800 bg-white text-slate-800"><ChevronRight className="h-5 w-5" /></button>
        </div>
        <div ref={track} data-testid="reviews-track"
          className="flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4 [scrollbar-width:thin]">
          {items.map((r, i) => <Card key={r.id} r={r} i={i} />)}
        </div>
      </div>
    </section>
  );
};
